/* eslint-disable react/react-in-jsx-scope */
import ProjectTypeSelectionDialog from "@/components/project/ProjectTypeSelectionDialog";
import { useToast } from "@/components/ui/use-toast";
import {
  removeProgramProjectFromProgramsInfoCache,
  upsertProgramProjectInProgramsInfoCache,
} from "@/dashboard/programQueryConfig";
import { useModal } from "@/dashboard/store/store";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { createProjectFromTypeSelection } from "@/modules/project-custom/createProjectFromType";
import { useTypeProjectContext } from "@/programs/context/typeProgramContext";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const ProjectCustomTypeDialog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { modals, programId, programName, setOpenModal } = useModal();
  const { typesProject } = useTypeProjectContext();
  const { fetchData } = useFetchDataV2("");
  const { postData } = usePostData<any>();
  const open = !!modals["projectCustomType"];
  const [isCreating, setIsCreating] = useState(false);

  const fallbackProgramId = (() => {
    const routeMatch = location.pathname.match(/^\/dashboard\/program\/(\d+)/);
    if (routeMatch?.[1]) {
      const parsed = Number(routeMatch[1]);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    const query = new URLSearchParams(location.search);
    const queryProgram = Number(query.get("programId"));
    if (Number.isFinite(queryProgram) && queryProgram > 0) return queryProgram;
    return undefined;
  })();

  const resolvedProgramId = programId ?? fallbackProgramId;
  const resolveProjectType = (typeId: number, typeLabel: string) => {
    const allTypes = Array.isArray(typesProject) ? typesProject : [];
    return (
      allTypes.find((item: any) => Number(item?.id) === Number(typeId)) ?? {
        id: typeId,
        image: null,
        name: typeLabel,
      }
    );
  };
  const handleClose = () => {
    setOpenModal({
      name: "projectCustomType",
      value: false,
      programId: resolvedProgramId,
      programName,
    });
  };

  const handleCreate = async ({ typeId, typeLabel }: { typeId: number; typeLabel: string }) => {
    if (!resolvedProgramId) {
      toast({
        className: "text-red-500",
        title: "Veuillez selectionner un programme avant de creer un projet.",
      });
      return;
    }
    if (!typeId) {
      toast({
        className: "text-red-500",
        title: "Veuillez selectionner un type de projet.",
      });
      return;
    }
    if (!typeLabel) {
      toast({
        className: "text-red-500",
        title: "Veuillez reessayer: type de projet introuvable.",
      });
      return;
    }

    setIsCreating(true);
    const tempProjectId = -Date.now();
    upsertProgramProjectInProgramsInfoCache(
      queryClient,
      resolvedProgramId,
      {
        id: tempProjectId,
        name: typeLabel,
        status: 1,
        soldStatus: 0,
        type: resolveProjectType(Number(typeId), typeLabel),
        isPersonnalized: true,
        personnalized: true,
        is_personnalized: true,
        __optimistic: true,
      },
    );
    handleClose();

    let result = null;
    try {
      result = await createProjectFromTypeSelection({
        fetchData,
        postData,
        programId: Number(resolvedProgramId),
        defaultProjectName: typeLabel,
        typeId: Number(typeId),
        allowCreateWithoutNativeFields: true,
      });
    } catch (error) {
      removeProgramProjectFromProgramsInfoCache(
        queryClient,
        resolvedProgramId,
        tempProjectId,
      );
      setOpenModal({
        name: "projectCustomType",
        value: true,
        programId: resolvedProgramId,
        programName,
      });
      toast({
        className: "text-red-500",
        title: "Veuillez reessayer: creation du projet impossible.",
      });
      return;
    } finally {
      setIsCreating(false);
    }

    if (!result?.success || !result.projectId) {
      removeProgramProjectFromProgramsInfoCache(
        queryClient,
        resolvedProgramId,
        tempProjectId,
      );
      setOpenModal({
        name: "projectCustomType",
        value: true,
        programId: resolvedProgramId,
        programName,
      });
      toast({
        className: "text-red-500",
        title: result?.message || "Veuillez reessayer: creation du projet impossible.",
      });
      return;
    }

    upsertProgramProjectInProgramsInfoCache(
      queryClient,
      resolvedProgramId,
      {
        id: Number(result.projectId),
        name: typeLabel,
        status: 1,
        soldStatus: 0,
        type: resolveProjectType(Number(typeId), typeLabel),
        isPersonnalized: true,
        personnalized: true,
        is_personnalized: true,
        __optimistic: false,
      },
      tempProjectId,
    );

    handleClose();
    navigate(`/dashboard/project/create-custom?programId=${resolvedProgramId}&projectId=${Number(result.projectId)}`);
  };

  return (
    <ProjectTypeSelectionDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      typesProject={Array.isArray(typesProject) ? typesProject : []}
      loading={isCreating}
      onConfirm={handleCreate}
    />
  );
};

export default ProjectCustomTypeDialog;
